/**
 * Extract scorecard detail data from MetaTunerState snapshots.
 *
 * The backend normalizes `state.current.context` (PromptIterationContext)
 * into `state.iterationContext` at ITERATION_END. Both paths carry the full
 * framework types: failureModes, mutations, executionResults.
 *
 * For BEFORE data (baseline), we look at `globalContext.context.executions.results`.
 * For AFTER data (post-mutation), we look at `iterContext.executionResults.executions`.
 */

// =============================================================================
// Types (matching scorecard-details.ts consumer shapes)
// =============================================================================

export interface ExtractedScorecardData {
  originalScore?: number;
  criteriaScores?: { name: string; before?: number; after?: number }[];
  datasetResults?: { id: string; beforeScore?: number; afterScore?: number }[];
  failureModes?: { category: string; failures: { description?: string; summary?: string }[] }[];
  mutations?: { label: string; status: 'applied' | 'rejected' | 'pending' | 'skipped'; priority?: string; rationale?: string }[];
  evaluationDetails?: {
    itemId: string; score: number; success: boolean;
    metrics?: {
      name: string; score: number; success: boolean;
      failureMode?: string; reasoning?: string;
      criteria?: { name: string; score: number; success: boolean }[];
    }[];
  }[];
}

// =============================================================================
// Main extraction function
// =============================================================================

/**
 * Extract scorecard detail fields from a raw MetaTunerState snapshot.
 *
 * @param rawState - The full state object from the `/states` API response
 * @param iterCtx - The iteration context (either from `state.iterationContext`
 *                   or `state.current.context`)
 */
export function extractScorecardDetails(
  rawState: Record<string, unknown>,
  iterCtx: Record<string, unknown> | undefined,
): ExtractedScorecardData {
  if (!iterCtx) return {};

  // --- BEFORE results (baseline from globalContext.context.executions) ---
  const gc = rawState.globalContext as Record<string, unknown> | undefined;
  const gcCtx = gc?.context as Record<string, unknown> | undefined;
  const beforeExec = gcCtx?.executions as { results?: Record<string, unknown>[] } | undefined;
  const beforeResults: Record<string, unknown>[] = beforeExec?.results ?? [];
  const beforeById = new Map<string, Record<string, unknown>>();
  for (const r of beforeResults) {
    const id = r.id as string | undefined;
    if (id) beforeById.set(id, r);
  }

  // Compute original (baseline) average score
  let originalScore: number | undefined;
  if (beforeResults.length > 0) {
    const sum = beforeResults.reduce((acc, r) => {
      const eval_ = r.evaluation as { score?: number } | undefined;
      return acc + (eval_?.score ?? 0);
    }, 0);
    originalScore = sum / beforeResults.length;
  }

  // --- AFTER results (post-mutation from iterContext.executionResults) ---
  const afterExec = iterCtx.executionResults as { executions?: Record<string, unknown>[] } | undefined;
  const afterResults: Record<string, unknown>[] = afterExec?.executions ?? [];

  // --- 1. Per-item before/after dataset results ---
  const datasetResults = afterResults.length > 0
    ? afterResults.map(r => {
        const id = (r.id as string) || 'unknown';
        const afterEval = r.evaluation as { score?: number } | undefined;
        const afterScore = afterEval?.score ?? 0;
        const beforeResult = beforeById.get(id);
        const beforeEval = beforeResult?.evaluation as { score?: number } | undefined;
        const beforeScore = beforeEval?.score;
        return { id, beforeScore, afterScore };
      })
    : undefined;

  // --- 2. Per-criterion before/after scores ---
  const criteriaScores = extractCriteriaScores(beforeResults, afterResults);

  // --- 3. Failure modes by category ---
  const rawFailureModes = iterCtx.failureModes as {
    categories?: string[];
    failures?: Record<string, { description?: string; summary?: string; label?: string }[]>;
  } | undefined;
  const failureModes = rawFailureModes?.categories && rawFailureModes.failures
    ? rawFailureModes.categories.map(category => ({
        category,
        failures: (rawFailureModes.failures?.[category] ?? []).map(f => ({
          description: f.description ?? f.label,
          summary: f.summary,
        })),
      }))
    : undefined;

  // --- 4. Mutations with status/priority/rationale ---
  const rawMutations = iterCtx.mutations as {
    label?: string;
    status?: string;
    priority?: string;
    target?: { rationale?: string };
  }[] | undefined;
  const mutations = rawMutations && rawMutations.length > 0
    ? rawMutations.map(m => ({
        label: m.label ?? 'Unknown mutation',
        status: (m.status ?? 'pending') as 'applied' | 'rejected' | 'pending' | 'skipped',
        priority: m.priority,
        rationale: m.target?.rationale,
      }))
    : undefined;

  // --- 5. Detailed evaluation breakdown per item ---
  const evaluationDetails = afterResults.length > 0
    ? afterResults.map(r => {
        const id = (r.id as string) || 'unknown';
        const eval_ = r.evaluation as {
          score?: number; success?: boolean;
          evaluations?: {
            name?: string; score?: number; success?: boolean;
            failureMode?: string; reasoning?: string;
            evaluationChecklist?: {
              items?: { evaluationParameter?: string; criteria?: string; llmScore?: number; success?: boolean }[];
            };
          }[];
        } | undefined;

        const score = eval_?.score ?? 0;
        const success = eval_?.success ?? false;
        const metrics = eval_?.evaluations?.map(metric => {
          const criteria = metric.evaluationChecklist?.items?.map(item => ({
            name: item.evaluationParameter ?? item.criteria ?? 'unknown',
            score: item.llmScore ?? 0,
            success: item.success ?? false,
          }));
          return {
            name: metric.name ?? 'unknown',
            score: metric.score ?? 0,
            success: metric.success ?? false,
            failureMode: metric.failureMode,
            reasoning: metric.reasoning,
            criteria: criteria && criteria.length > 0 ? criteria : undefined,
          };
        });

        return {
          itemId: id,
          score,
          success,
          metrics: metrics && metrics.length > 0 ? metrics : undefined,
        };
      })
    : undefined;

  return {
    originalScore,
    criteriaScores,
    datasetResults,
    failureModes,
    mutations,
    evaluationDetails,
  };
}

// =============================================================================
// Per-criterion score aggregation
// =============================================================================

/**
 * Extract per-criterion before/after scores by aggregating metric-level
 * evaluations across all dataset items.
 *
 * Each PromptExecution has evaluation.evaluations[] (PromptEvaluationMetricResult),
 * each with a name and score. We aggregate by metric name across all items.
 */
function extractCriteriaScores(
  beforeResults: Record<string, unknown>[],
  afterResults: Record<string, unknown>[],
): { name: string; before?: number; after?: number }[] | undefined {
  const metricNames = new Set<string>();
  const beforeScores = new Map<string, number[]>();
  const afterScores = new Map<string, number[]>();

  for (const r of beforeResults) {
    const eval_ = r.evaluation as { evaluations?: { name?: string; score?: number }[] } | undefined;
    for (const m of eval_?.evaluations ?? []) {
      const name = m.name ?? 'unknown';
      metricNames.add(name);
      const existing = beforeScores.get(name) ?? [];
      existing.push(m.score ?? 0);
      beforeScores.set(name, existing);
    }
  }
  for (const r of afterResults) {
    const eval_ = r.evaluation as { evaluations?: { name?: string; score?: number }[] } | undefined;
    for (const m of eval_?.evaluations ?? []) {
      const name = m.name ?? 'unknown';
      metricNames.add(name);
      const existing = afterScores.get(name) ?? [];
      existing.push(m.score ?? 0);
      afterScores.set(name, existing);
    }
  }

  if (metricNames.size === 0) return undefined;

  const avg = (arr: number[]): number => arr.reduce((a, b) => a + b, 0) / arr.length;

  return Array.from(metricNames).map(name => ({
    name,
    before: beforeScores.has(name) ? avg(beforeScores.get(name) ?? []) : undefined,
    after: afterScores.has(name) ? avg(afterScores.get(name) ?? []) : undefined,
  }));
}
